"use client";

import { usePathname, useSearchParams } from "next/navigation";

import { useGenieCasesEnabled } from "./modules/cases";
import { useGenieContext } from "./use-genie-context";

/**
 * The page surfaces Genie preloads skills for. Derived here from the route
 * rather than declared page by page: the editor's sub-pages are tabs in the
 * `?tab=` query param, not separate routes, so the URL already says everything.
 * The backend maps each `is_<surface>` flag to the skill that page needs.
 */
export interface GenieSurfaces {
  is_workflow_editor: boolean;
  is_workflow_editor_evals: boolean;
  is_agent_editor: boolean;
  is_tables: boolean;
  is_knowledge_bases: boolean;
  is_cases: boolean;
}

export function deriveGenieSurfaces(
  pathname: string | null,
  tab: string | null
): GenieSurfaces {
  const path = pathname ?? "";
  const isWorkflowEditor = /^\/flows\/[^/]+/.test(path);
  return {
    is_workflow_editor: isWorkflowEditor,
    is_workflow_editor_evals: isWorkflowEditor && tab === "evals",
    is_agent_editor: /^\/assistant\/[^/]+/.test(path),
    is_tables: /^\/tables(\/|$)/.test(path),
    is_knowledge_bases:
      /^\/data-hub\/[^/]+/.test(path) ||
      (path === "/" && tab === "knowledge-bases"),
    is_cases: /^\/(inbox|cases|tasks)(\/|$)/.test(path),
  };
}

export function useGenieSurfaces(): void {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const surfaces = deriveGenieSurfaces(
    pathname,
    searchParams?.get("tab") ?? null
  );

  useGenieContext(
    "is_workflow_editor",
    "The user is in the workflow editor",
    surfaces.is_workflow_editor
  );
  useGenieContext(
    "is_workflow_editor_evals",
    "The user is on the workflow editor's Tests tab",
    surfaces.is_workflow_editor_evals
  );
  useGenieContext(
    "is_agent_editor",
    "The user is configuring an agent",
    surfaces.is_agent_editor
  );
  useGenieContext(
    "is_tables",
    "The user is in data tables",
    surfaces.is_tables
  );
  useGenieContext(
    "is_knowledge_bases",
    "The user is in knowledge bases",
    surfaces.is_knowledge_bases
  );
  // Off with Genie's case tools, so no case guide is preloaded either.
  const casesOn = useGenieCasesEnabled();
  useGenieContext(
    "is_cases",
    "The user is in case management: an inbox, a case or a task",
    surfaces.is_cases && casesOn
  );
}
